import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom'
import { LeaveConfirmationModal } from '../components/common'

const defaultContext = {
    isBlocked: false,
    isModalVisible: false,
    blockedPathname: '',
    block: () => () => {},
    isComponentBlocked: () => false,
    unblockComponent: () => {},
}

const PreventNavigationContext = createContext(defaultContext)

export const useNavigationContext = () => {
    const context = useContext(PreventNavigationContext)
    if (!context) {
        throw new Error(
            'Navigation context not found. Make sure the component is nested inside the "PreventNavigationProvider"'
        )
    }
    return context
}

// Custom navigation blocker for React Router v6
function useBlocker(blocker, when = true) {
    const navigator = useContext(NavigationContext).navigator
    useEffect(() => {
        if (!when) return

        const unblock = navigator.block(tx => {
            const autoUnblock = () => unblock()
            blocker({
                ...tx,
                retry: () => {
                    autoUnblock()
                    tx.retry()
                },
            })
        })

        return unblock
    }, [navigator, blocker, when])
}

let components = {}

const isAnyBlocked = () => Object.values(components).some(Boolean)

const preventUnload = e => {
    e.preventDefault()
    e.returnValue = ''
}

const PreventNavigationProvider = ({ children }) => {
    const [isBlocked, setIsBlocked] = useState(false)
    const [isModalVisible, setIsModalVisible] = useState(false)
    const navigate = useNavigate()
    const nextLocationRef = useRef(null)

    const unblockComponent = useCallback(component => {
        components = {
            ...components,
            [component]: false,
        }

        const stillBlocked = isAnyBlocked()
        if (!stillBlocked) {
            window.removeEventListener('beforeunload', preventUnload)
        }

        setIsBlocked(stillBlocked)
    }, [])

    const block = useCallback(
        component => {
            if (components[component]) return () => unblockComponent(component)

            components = {
                ...components,
                [component]: true,
            }

            setIsBlocked(true)
            window.addEventListener('beforeunload', preventUnload)

            return () => unblockComponent(component)
        },
        [unblockComponent]
    )

    const isComponentBlocked = useCallback(component => !!components[component], [])

    const onLeave = useCallback(() => {
        window.removeEventListener('beforeunload', preventUnload)
        components = {}
        setIsBlocked(false)
        setIsModalVisible(false)
        if (nextLocationRef.current) {
            navigate(nextLocationRef.current.pathname)
        }
    }, [navigate])

    const onCancel = useCallback(() => {
        nextLocationRef.current = null
        setIsModalVisible(false)
    }, [])

    useBlocker(tx => {
        if (isBlocked) {
            nextLocationRef.current = tx.location
            setIsModalVisible(true)
        } else {
            tx.retry()
        }
    }, isBlocked)

    return (
        <>
            <LeaveConfirmationModal
                show={isModalVisible}
                leaveClickHandler={onLeave}
                cancelAlertModal={onCancel}
            />
            <PreventNavigationContext.Provider
                value={{
                    isBlocked,
                    isModalVisible,
                    blockedPathname: nextLocationRef.current?.pathname || '',
                    block,
                    unblockComponent,
                    isComponentBlocked,
                }}
            >
                {children}
            </PreventNavigationContext.Provider>
        </>
    )
}

export default PreventNavigationProvider
